import { environment } from 'src/environments/environment';

/**
 * Data-residency Phase 2: the same SPA build is served from each region host
 * (US prod `leap.appliedparticle.io`, HQ/global `global-app.leap.appliedparticle.io`,
 * CA `ca.leap.appliedparticle.io`). Each must talk to its OWN region API, so the
 * API base URL is derived from the web host at runtime instead of being baked
 * into the build. Non-production hosts (localhost, staging, dev) are not in the
 * map and fall back to the environment config, so their behaviour is unchanged.
 *
 * The region-local JWT needs no special handling here: tokens live in
 * localStorage, which browsers isolate per origin, so each region host keeps
 * its own token and never sends it to another region's API.
 *
 * Hostnames confirmed by infra (RESIDENCY-FE-HANDOFF.md): the HQ/global app
 * node is `global-app.leap.appliedparticle.io` -> `global.leap-api...`; CA is
 * `ca.leap.appliedparticle.io` -> `ca.leap-api...`. The bare `leap.…` entry is
 * the existing US production app (left unchanged). NOTE: the global app lives on
 * `global-app.…`, NOT `global.leap.appliedparticle.io` — that origin is owned by
 * the standalone region picker (region-picker/README.md), so the app cannot
 * share it or the Entra auth callback would land on the picker (DASH-3098).
 */
const REGION_API_BASE_BY_WEB_HOST: Readonly<Record<string, string>> = {
    'leap.appliedparticle.io': 'https://leap-api.appliedparticle.io:443',
    'global-app.leap.appliedparticle.io': 'https://global.leap-api.appliedparticle.io:443',
    'ca.leap.appliedparticle.io': 'https://ca.leap-api.appliedparticle.io:443',
};

/** Pure host→apiUrl resolution (testable). `/api` suffix, mirrors `environment.apiUrl`. */
export function resolveRegionApiUrl(host: string): string {
    const base = REGION_API_BASE_BY_WEB_HOST[host];
    return base ? `${base}/api` : environment.apiUrl;
}

/** Pure host→baseUrl resolution (testable). No `/api`, mirrors `environment.baseUrl`. */
export function resolveRegionBaseUrl(host: string): string {
    return REGION_API_BASE_BY_WEB_HOST[host] ?? environment.baseUrl;
}

function currentHost(): string {
    return typeof window !== 'undefined' ? window.location.hostname : '';
}

/** API base including the `/api` suffix for the current region host. */
export function getRegionApiUrl(): string {
    return resolveRegionApiUrl(currentHost());
}

/** Server base without the `/api` suffix for the current region host. */
export function getRegionBaseUrl(): string {
    return resolveRegionBaseUrl(currentHost());
}
